function Mapeo(options = {}) {
	if (typeof mapboxgl === undefined || mapboxgl === null) {
		throw new Error("The 'Mapbox GL JS' SDK is required to initialize Mapeo");
	}
	this.settings = {
		// Define default settings
		mapboxToken: "",
		containerId: "mapeo",
		initialState: {
			centerCoords: [-100.315, 25.68], // MTY, NL [lng, lat]
			zoomLevel: 12,
			styleUrl: "mapbox://styles/mapbox/dark-v11",
		},
		imageSources: {},
		dataSource: "",
		dataSet: [],
		markerVariants: [],
		markerImages: [],
		// Add the optional input settings
		...options,
	};
	// Create the mapbox map instance
	// The mapbox configuration object contains the following properties:
	//  - container{string}: the ID from the HTML element that will contain the map
	//  - center{array<number>}: starting position coords in [longitude, latitude] format
	//  - zoom{number}: initial zoom level
	//  - style{string}: the URL of the map style configuration which defines the layers appearance.
	mapboxgl.accessToken = this.settings.mapboxToken;
	const { centerCoords, zoomLevel, styleUrl } = this.settings.initialState;
	this.map = new mapboxgl.Map({
		container: this.settings.containerId,
		center: centerCoords,
		zoom: zoomLevel,
		style: styleUrl,
	});
	// Create the popup instance
	this.popup = new mapboxgl.Popup({
		offset: 30,
		focusAfterOpen: false,
		closeOnClick: false,
		anchor: "bottom",
	});
	// Create the geolocate popup instance
	this.geolocatePopup = new mapboxgl.Popup({
		offset: 20,
		focusAfterOpen: false,
		closeOnClick: false,
		anchor: "bottom",
	});
	// Create the marker data collection
	this.markerData = {};
	// marker and cluster state handlers
	this.featureState = {
		selectedMarkerId: null,
		highlightedMarkerId: null,
		highlightedClusterId: null,
	};
	// store the marker layers for each variant
	this.markerLayers = [];
	// Create the geolocalization control instance
	this.geolocate = new mapboxgl.GeolocateControl({
		positionOptions: {
			enableHighAccuracy: true,
		},
		trackUserLocation: true,
	});
	this.map.addControl(this.geolocate);
}

// Initialize the map
Mapeo.prototype.load = async function (callback) {
	// Load all images used in the map before adding any layer
	try {
		this.markerData = this.getPointCollection();
		const loadedImages = await this.loadImages();
		this.map.on("load", () => {
			//this.markerData = this.getPointCollection();
			this.setLayers({ images: loadedImages });
			this.setEvents();
			callback(this);
		});
	} catch (error) {
		console.error(error);
	}
};

// Queue the image sources to be loaded before being used on the map
Mapeo.prototype.loadImages = function () {
	return new Promise((resolve, reject) => {
		const loadedImages = {};
		const imageSources = Object.entries(this.settings.imageSources);
		let loadedAttempts = 0;
		if (!imageSources || !imageSources.length) {
			resolve(loadedImages);
		}
		imageSources.forEach(([id, url]) => {
			this.map.loadImage(url, (error, image) => {
				// exit the promise if any of the images fail loading
				if (error) {
					console.log(`Map error: image ${id} could not load.`);
					console.log(error);
					//reject(error);
				} else {
					// store the resulting image data once its loaded
					loadedImages[id] = image;
				}
				// exit the promise once all of the images attempted to load
				if (
					++loadedAttempts === Object.keys(this.settings.imageSources).length
				) {
					resolve(loadedImages);
				}
			});
		});
	});
};

Mapeo.prototype.getPointCollection = function (dataset) {
	if (this.settings.dataSource && this.settings.dataSource != "") {
		return this.settings.dataSource;
	}
	if (!this.settings.dataSet || !this.settings.dataSet.length) {
		return false;
	}
	const pointFeatures = this.settings.dataSet.map((feature) => {
		const [lng, lat] = feature.coordinates;
		const point = {
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: [lng, lat],
			},
		};
		if (feature.hasOwnProperty("properties")) {
			point.properties = {};
			Object.entries(feature.properties).forEach(([property, value]) => {
				point.properties[property] = value;
			});
		}
		return point;
	});

	return {
		type: "FeatureCollection",
		features: pointFeatures,
	};
};

Mapeo.prototype.setLayers = function (resources) {
	const { images } = resources;
	// add marker background image
	/* const defaultMarkerId = "marker-default";
  if (!this.map.hasImage(defaultMarkerId)) {
    this.map.addImage(defaultMarkerId, images[defaultMarkerId], {
      content: [7, 5, 8, 6],
      stretchX: [[7, 8]],
      stretchY: [[5, 6]],
    });
  } */
	// add marker background images based on the marker variants
	this.settings.markerVariants.forEach((variant) => {
		if (!images.hasOwnProperty(variant.iconImageId)) return;
		if (this.map.hasImage(variant.iconImageId)) return;
		/* this.map.addImage(
			variant.iconImageId,
			images[variant.iconImageId],
			variant.iconTransform ?? {
				content: [11, 5, 12, 18],
				stretchX: [[11, 12]],
				stretchY: [[11, 12]],
			},
		); */
		this.map.addImage(variant.iconImageId, images[variant.iconImageId]);
		const markerLayer = {
			name: variant.iconImageId,
			textColor: variant.textColor,
		};
		if (variant.hasOwnProperty("targetProperty")) {
			markerLayer.filter = variant.targetProperty;
		}
		this.markerLayers.push(markerLayer);
	});
	this.settings.markerImages.forEach((marker) => {
		if (!images.hasOwnProperty(marker)) return;
		if (this.map.hasImage(marker)) return;
		this.map.addImage(marker, images[marker]);
	});
	// global active marker for user interaction feedback
	// add active marker background image
	const activeMarkerId = "marker-active";
	if (
		!this.map.hasImage(activeMarkerId) &&
		images.hasOwnProperty(activeMarkerId)
	) {
		/* this.map.addImage(activeMarkerId, images[activeMarkerId], {
			content: [11, 5, 12, 18],
			stretchX: [[11, 12]],
			stretchY: [[11, 12]],
		}); */
		this.map.addImage(activeMarkerId, images[activeMarkerId]);
	}
	// load marker data and duplicate the source to mutate cluster data when filtering
	this.map.addSource("clustered-data", {
		type: "geojson",
		data: this.markerData,
		cluster: true,
		clusterMaxZoom: 14,
		clusterRadius: 100,
		generateId: true,
	});
	this.map.addSource("unclustered-data", {
		type: "geojson",
		data: this.markerData,
		generateId: true,
	});
	// hidden layer needed to query unclustered features later for filtering
	this.map.addLayer({
		id: "unclustered-markers",
		type: "circle",
		source: "unclustered-data",
		paint: {
			"circle-opacity": 0,
		},
	});
	// clustered markers layers
	this.map.addLayer({
		id: "cluster-icons",
		type: "circle",
		source: "clustered-data",
		filter: ["boolean", ["get", "cluster"], false],
		paint: {
			"circle-color": [
				"case",
				["boolean", ["feature-state", "highlighted"], false],
				"#131011",
				"#eeedee",
			],
			"circle-stroke-color": "#131011",
			"circle-stroke-width": 1,
			"circle-radius": 24,
		},
	});
	this.map.addLayer({
		id: "cluster-labels",
		type: "symbol",
		source: "clustered-data",
		filter: ["boolean", ["get", "cluster"], false],
		layout: {
			"text-field": ["get", "point_count_abbreviated"],
			"text-font": ["Open Sans Semibold"],
			"text-size": 12,
			"icon-ignore-placement": true,
			"text-allow-overlap": true,
		},
		paint: {
			"text-color": [
				"case",
				["boolean", ["feature-state", "highlighted"], false],
				"#cccccc",
				"#1c1c1c",
			],
		},
	});
	// individual markers layers
	this.map.addLayer({
		id: "active-markers",
		type: "symbol",
		source: "clustered-data",
		filter: ["!", ["boolean", ["get", "cluster"], false]],
		layout: {
			"icon-image": "marker-active",
			//"icon-image": ["concat", "marker-", ["get", "id"]],
			//"icon-size": 0.3,
			"text-field": ["get", "index"],
      "text-font": ["Open Sans Semibold"],
      "text-size": 14,
      "text-ignore-placement": true,
      //"icon-text-fit": "both",
			"icon-ignore-placement": true,
		},
		paint: {
			"text-color": "#ffffff",
      "icon-halo-width": 2,
      "icon-halo-blur": 4,
      "icon-halo-color": "#131011",
			"icon-color-contrast": -0.5,
			"icon-opacity": [
				"case",
				["boolean", ["feature-state", "selected"], false],
				1,
				["boolean", ["feature-state", "highlighted"], false],
				1,
				0,
			],
		},
	});
	this.markerLayers.forEach((markerLayer) => {
		const layerConfig = {
			id: markerLayer.name,
			type: "symbol",
			source: "clustered-data",
			layout: {
				"icon-image": markerLayer.name,
				//"icon-image": ["concat", "marker-", ["get", "id"]],
				//"icon-size": 0.3,
				"text-field": ["get", "index"],
				"text-font": ["Open Sans Semibold"],
				"text-size": 14,
				//"icon-text-fit": "both",
				"icon-ignore-placement": true,
				"text-ignore-placement": true,
			},
			paint: {
				"text-color": markerLayer.textColor,
				"icon-opacity": [
					"case",
					["boolean", ["feature-state", "selected"], false],
					0,
					["boolean", ["feature-state", "highlighted"], false],
					0,
					1,
				],
				"text-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          0,
          ["boolean", ["feature-state", "highlighted"], false],
          0,
          1,
        ]
				/* "icon-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          0,
          ["boolean", ["feature-state", "highlighted"], false],
          0,
          1,
        ],
        "text-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          0,
          ["boolean", ["feature-state", "highlighted"], false],
          0,
          1,
        ], */
			},
		};
		layerConfig.filter = markerLayer.hasOwnProperty("filter")
			? [
					"all",
					["in", markerLayer.filter.name, markerLayer.filter.value],
					["!has", "cluster"],
				]
			: ["!has", "cluster"];
		this.map.addLayer(layerConfig);
	});
};

Mapeo.prototype.setEvents = function () {
	// set marker click event
	this.map.on("click", (event) => {
		// attach cluster event to expand on click
		const clusters = this.map.queryRenderedFeatures(event.point, {
			layers: ["cluster-icons"],
		});
		if (clusters && clusters.length) {
			// get the first feature present on the point clicked representing the cluster
			const [cluster] = clusters;
			// get the zoom level needed to break the cluster into smaller clusters or individual markers, then move the camera
			this.map
				.getSource("clustered-data")
				.getClusterExpansionZoom(
					cluster.properties.cluster_id,
					(error, zoom) => {
						if (error) {
							return console.error(error);
						}
						this.map.easeTo({
							center: cluster.geometry.coordinates,
							zoom: zoom,
						});
					},
				);
		}
		// unselect the active marker and escape the listener if there is no features present on the point clicked
		const markers = this.map.queryRenderedFeatures(event.point, {
			layers: this.markerLayers.map((markerLayer) => markerLayer.name),
		});
		if (!markers || !markers.length) {
			if (
				this.featureState.selectedMarkerId ||
				this.featureState.selectedMarkerId === 0
			) {
				this.toggleSelectedMarker(
					{ id: this.featureState.selectedMarkerId },
					false,
				);
			}
			return;
		}
		// get the first feature present on the point clicked representing the marker and set its state
		const [marker] = markers;
		console.log(marker);
		// unselect the active marker if there is any selected
		if (
			this.featureState.selectedMarkerId ||
			this.featureState.selectedMarkerId === 0
		) {
			if (this.featureState.selectedMarkerId === marker.id) {
				return;
			}
			this.toggleSelectedMarker(
				{ id: this.featureState.selectedMarkerId },
				false,
			);
		}
		this.toggleSelectedMarker(marker, true);
	});
	// set marker mouse events for each variant layer
	this.markerLayers.forEach((markerLayer) => {
		// set marker hover event
		this.map.on("mouseenter", markerLayer.name, (event) => {
			// escape the listener if there is no features present on the point being hovered
			if (!event.hasOwnProperty("features") || !event.features.length) {
				return;
			}
			this.map.getCanvas().style.cursor = "pointer";
			// get the first feature present on the point hovered representing the marker
			const [marker] = event.features;
			// handle feature state
			if (
				this.featureState.highlightedMarkerId ||
				this.featureState.highlightedMarkerId === 0
			) {
				if (this.featureState.highlightedMarkerId === marker.id) {
					return;
				}
				this.toggleFeatureState(
					this.featureState.highlightedMarkerId,
					"highlighted",
					false,
				);
			}
			this.toggleFeatureState(marker.id, "highlighted", true);
			this.featureState.highlightedMarkerId = marker.id;
		});
		this.map.on("mouseleave", markerLayer.name, (event) => {
			this.map.getCanvas().style.cursor = "";
			// escape the listener if there was no feature being hovered
			if (this.featureState.highlightedMarkerId === null) {
				return;
			}
			this.toggleFeatureState(
				this.featureState.highlightedMarkerId,
				"highlighted",
				false,
			);
			this.featureState.highlightedMarkerId = null;
		});
	});
	// set cluster hover event
	this.map.on("mouseenter", "cluster-icons", (event) => {
		// escape the listener if there is no features present on the point being hovered
		if (!event.hasOwnProperty("features") || !event.features.length) {
			return;
		}
		this.map.getCanvas().style.cursor = "pointer";
		// get the first feature present on the point hovered representing the marker
		const [cluster] = event.features;
		// handle feature state
		if (
			this.featureState.highlightedClusterId ||
			this.featureState.highlightedClusterId === 0
		) {
			if (this.featureState.highlightedClusterId === cluster.id) {
				return;
			}
			this.toggleFeatureState(
				this.featureState.highlightedClusterId,
				"highlighted",
				false,
			);
		}
		this.toggleFeatureState(cluster.id, "highlighted", true);
		this.featureState.highlightedClusterId = cluster.id;
	});
	this.map.on("mouseleave", "cluster-icons", (event) => {
		this.map.getCanvas().style.cursor = "";
		// escape the listener if there was no feature being hovered
		if (this.featureState.highlightedClusterId === null) {
			return;
		}
		this.toggleFeatureState(
			this.featureState.highlightedClusterId,
			"highlighted",
			false,
		);
		this.featureState.highlightedClusterId = null;
	});
	// set popup close event
	this.popup.on("close", (event) => {
		// unselect the active marker corresponding to the current popup anchor
		if (
			this.featureState.selectedMarkerId ||
			this.featureState.selectedMarkerId === 0
		) {
			this.toggleSelectedMarker(
				{ id: this.featureState.selectedMarkerId },
				false,
			);
		}
	});
	// set geolocalization event
	this.geolocate.on("geolocate", (event) => {
		const { longitude, latitude } = event.coords;
		// open pop-up in feature position
		this.geolocatePopup
			.setLngLat([longitude, latitude])
			.setDOMContent(this.getGeolocatePopupElement(longitude, latitude));
		if (!this.geolocatePopup.isOpen()) {
			this.geolocatePopup.addTo(this.map);
		}
	});
};

Mapeo.prototype.toggleFeatureState = function (featureId, state, value) {
	const update = {};
	update[state] = value;
	this.map.setFeatureState(
		{
			source: "clustered-data",
			id: featureId,
		},
		update,
	);
};

Mapeo.prototype.toggleSelectedMarker = function (feature, selected) {
	this.toggleFeatureState(feature.id, "selected", selected);
	if (selected) {
		this.featureState.selectedMarkerId = feature.id;
		const [lng, lat] = feature.geometry.coordinates;
		// move camera to feature position
		this.map.easeTo({
			center: [lng, lat],
			padding: { top: 100, bottom: 0, left: 0, right: 0 },
			retainPadding: false,
		});
		// open pop-up in feature position
		this.popup
			.setLngLat([lng, lat])
			.setDOMContent(this.getPopupElement(feature))
			.addTo(this.map);
	} else {
		this.featureState.selectedMarkerId = null;
		// remove active pop-up
		if (this.popup.isOpen()) {
			this.popup.remove();
		}
	}
};

Mapeo.prototype.filterMarkers = function (filters) {
	// get a list of mapbox expressions representing the filters to be matched
	const filterExpressions = filters.map(this.getFilterExpression);
	const matchingExpression = ["all", ...filterExpressions];
	// get filtered markers from the unclustured reference source to update clustered data
	const filteredMarkers = this.map.querySourceFeatures("unclustered-data", {
		filter: matchingExpression,
	});
	const uniqueMarkers = this.getUniqueFeatures(filteredMarkers);
	this.map.getSource("clustered-data").setData({
		type: "FeatureCollection",
		features: uniqueMarkers,
	});
};

Mapeo.prototype.getFilterExpression = function ({ property, values }) {
	const expression = values.length
		? ["in", property, ...values]
		: ["has", property];

	return expression;
};

Mapeo.prototype.getPopupElement = function (feature) {
	const properties = feature.properties;
	// popup container
	const element = document.createElement("div");
	element.className = "mapbox-popup max-w-normal lg:max-w-wide w-full bg-white border border-near-black grid grid-cols-3 p-2 gap-x-2"
	// image
	if (properties.thumbnail) {
		const image = document.createElement("img");
		image.className = "aspect-square object-cover select-none";
		image.src = properties.thumbnail;
		element.appendChild(image);
	}
	
	// text wrapper
	const descriptionWrapper = document.createElement("div");
	descriptionWrapper.className = "text-sm lg:text-base text-near-black flex flex-col justify-center col-span-2 gap-y-3";
	// title
	if (properties.title && properties.title != "") {
		const title = document.createElement("p");
		title.className = "uppercase";
		title.textContent = properties.title;
		descriptionWrapper.appendChild(title);
	}
	if (properties.address && properties.address != "") {
		const address = document.createElement("div");
		address.innerHTML = properties.address;
		descriptionWrapper.appendChild(address);
	}
	if (properties.url && properties.url != "") {
		const link = document.createElement("a");
		link.href = properties.url;
		link.target = "_blank";
		link.className = "underline uppercase";
		link.textContent = "Directions";
		descriptionWrapper.appendChild(link);
	}

	element.appendChild(descriptionWrapper);

	return element;
};

Mapeo.prototype.getGeolocatePopupElement = function (lng, lat) {
	const element = document.createElement("div");
	element.classList.add("mapbox-popup");
	// title
	const title = document.createElement("h1");
	title.textContent = "Tus coordenadas son:";
	element.appendChild(title);
	// description
	const longitude = document.createElement("p");
	longitude.textContent = `Lng: ${lng}`;
	element.appendChild(longitude);
	const latitude = document.createElement("p");
	latitude.textContent = `Lat: ${lat}`;
	element.appendChild(latitude);
	// cta
	const cta = document.createElement("a");
	cta.href = "#";
	cta.textContent = "Enviar";
	element.appendChild(cta);
	cta.addEventListener("click", (event) => {
		event.preventDefault();
	});

	return element;
};

Mapeo.prototype.getUniqueFeatures = function (features) {
	const uniqueIds = new Set();
	const uniqueFeatures = [];
	features.forEach((feature) => {
		if (uniqueIds.has(feature.id)) {
			return;
		}
		uniqueIds.add(feature.id);
		uniqueFeatures.push(feature);
	});

	return uniqueFeatures;
};
